import apiClient from './client';

export interface Branch {
  id: string;
  name: string;
  code: string;
  address?: string;
  city?: string;
  phone?: string;
  company?: string | any;
  isHeadquarters?: boolean;
  isActive: boolean;
  documentCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBranchRequest {
  name: string;
  code: string;
  address?: string;
  city?: string;
  phone?: string;
  company?: string;
  isHeadquarters?: boolean;
}

export interface UpdateBranchRequest {
  name?: string;
  code?: string;
  address?: string;
  city?: string;
  phone?: string;
  company?: string;
  isHeadquarters?: boolean;
  isActive?: boolean;
}

export interface SyncResult {
  synced: number;
  created: number;
  updated: number;
}

export interface ImportResult {
  imported: number;
  created: number;
  updated: number;
  errors: string[];
}

export interface BranchDocument {
  id: string;
  branchId: string;
  fileName: string;
  fileUrl: string;
  fileKey: string;
  fileType: string;
  fileSize: number;
  description?: string;
  uploadedBy?: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export const branchesApi = {
  getAll: async (): Promise<Branch[]> => {
    const response = await apiClient.get('/branches');
    return response.data.map((item: any) => ({ 
      ...item, 
      id: item._id,
      documentCount: item.documentCount || 0,
    }));
  },

  getById: async (id: string): Promise<Branch> => {
    const response = await apiClient.get(`/branches/${id}`);
    return { ...response.data, id: response.data._id };
  },

  create: async (data: CreateBranchRequest): Promise<Branch> => {
    const response = await apiClient.post('/branches', data);
    return { ...response.data, id: response.data._id };
  },

  update: async (id: string, data: UpdateBranchRequest): Promise<Branch> => {
    const response = await apiClient.put(`/branches/${id}`, data);
    return { ...response.data, id: response.data._id };
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/branches/${id}`);
  },

  syncFrom360: async (): Promise<SyncResult> => {
    const response = await apiClient.post('/branches/sync-from-360');
    return response.data;
  },

  importFromExcel: async (file: File): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/branches/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadTemplate: async (): Promise<void> => {
    const response = await apiClient.get('/branches/download-template', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'subeler-sablonu.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Document management
  getDocuments: async (branchId: string): Promise<BranchDocument[]> => {
    const response = await apiClient.get(`/branches/${branchId}/documents`);
    return response.data.map((item: any) => ({ ...item, id: item._id }));
  },

  uploadDocument: async (
    branchId: string,
    file: File,
    description?: string,
  ): Promise<BranchDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    const response = await apiClient.post(
      `/branches/${branchId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return { ...response.data, id: response.data._id };
  },

  deleteDocument: async (branchId: string, documentId: string): Promise<void> => {
    await apiClient.delete(`/branches/${branchId}/documents/${documentId}`);
  },
};
